import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

export interface ComplianceItem {
  label: string;
  value: number;
  status: 'ok' | 'warning' | 'critical';
  icon: string;
  route?: string;
}

@Component({
  selector: 'app-compliance-tracker',
  standalone: true,
  imports: [NgClass, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './compliance-tracker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplianceTrackerComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() widgetId = 'complianceHealth';
  @Input() filters: any = {};
  @Input() title = 'Compliance Status';
  @Input() items: ComplianceItem[] = []; // Can be passed directly
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  loading = false;
  error: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (this.dashboardType && this.items.length === 0) this.fetchData();
  }

  private fetchData(): void {
    this.loading = true;
    this.engine
      .getWidgetData(this.dashboardType, this.widgetId, this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data ?? res.record ?? {};
          this.items = this.mapToItems(d);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : err.status === 500 ? 'Service unavailable' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private mapToItems(d: any): ComplianceItem[] {
    const all: (ComplianceItem & { key: string })[] = [
      {
        key: 'pending_regulatory_submissions',
        label: 'Pending Regulatory Submissions',
        value: d.pending_regulatory_submissions ?? 0,
        status: d.pending_regulatory_submissions > 0 ? 'critical' : 'ok',
        icon: 'send',
      },
      {
        key: 'communities_missing_documents',
        label: 'Communities Missing Documents',
        value: d.communities_missing_documents ?? 0,
        status: d.communities_missing_documents > 0 ? 'warning' : 'ok',
        icon: 'folder_off',
      },
      { key: 'open_audit_queries', label: 'Open Audit Queries', value: d.open_audit_queries ?? 0, status: d.open_audit_queries > 0 ? 'warning' : 'ok', icon: 'manage_search' },
    ];
    // Only include items whose key was explicitly returned by the backend
    return all.filter((item) => item.key in d).map(({ key, ...rest }) => rest as ComplianceItem);
  }

  getStatusClasses(status: string): { icon: string; text: string; bg: string } {
    return (
      {
        ok: { icon: 'check_circle', text: 'text-green-600', bg: 'bg-green-50' },
        warning: { icon: 'warning', text: 'text-yellow-600', bg: 'bg-yellow-50' },
        critical: { icon: 'error', text: 'text-red-600', bg: 'bg-red-50' },
      }[status] ?? { icon: 'info', text: 'text-blue-600', bg: 'bg-blue-50' }
    );
  }
}
